/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  Building2, Users, Calendar, MessageSquare, Receipt, FileClock,
  Mail, CheckCircle, Clock, XCircle, AlertTriangle, ShieldCheck,
  Ban, Play, CalendarPlus, ExternalLink, KeyRound, MailPlus, Wrench,
  Activity, Send,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import ActivityTimeline from "./ActivityTimeline";

const fmt = (iso) => { try { return iso ? format(new Date(iso), "MMM d, yyyy HH:mm") : "—"; } catch { return iso || "—"; } };
const fmtDate = (iso) => { try { return iso ? format(new Date(iso), "MMM d, yyyy") : "—"; } catch { return iso || "—"; } };
const money = (n, ccy = "GBP") => new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(n || 0);

function StatusBadge({ status }) {
  if (status === "active") return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
  if (status === "trial") return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Clock className="w-3 h-3 mr-1" />Trial</Badge>;
  if (status === "expired") return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
  if (status === "cancelled") return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30"><AlertTriangle className="w-3 h-3 mr-1" />Cancelled</Badge>;
  return <Badge variant="outline">{status || "—"}</Badge>;
}

export default function BandDetailDrawer({ open, onOpenChange, bandId, api, onActionDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  // iter-104: daily-digest diagnostic state
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  // iter-105: stripe events log
  const [stripeEvents, setStripeEvents] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  const reload = async () => {
    if (!bandId) return;
    setLoading(true);
    try {
      const r = await api.get(`/system/bands/${bandId}`);
      setData(r.data);
    } catch { /* parent surfaces errors */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !bandId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setDigest(null);
    setStripeEvents(null);
    (async () => {
      try {
        const r = await api.get(`/system/bands/${bandId}`);
        if (!cancelled) setData(r.data);
      } catch {
        /* parent surfaces errors via interceptor */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, bandId, api]);

  const runAction = async (label, fn) => {
    setActionLoading(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || `${label} complete`);
      await reload();
      onActionDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    } finally {
      setActionLoading(false);
    }
  };

  const onSuspend = () => runAction("Suspend",
    () => api.post(`/system/bands/${bandId}/suspend`));
  const onUnsuspend = () => runAction("Un-suspend",
    () => api.post(`/system/bands/${bandId}/unsuspend`));
  const onExtendTrial = (days) => runAction(`Extend trial +${days}d`,
    () => api.post(`/system/bands/${bandId}/extend-trial`, { days }));
  const onImpersonate = async () => {
    setActionLoading(true);
    try {
      const r = await api.post(`/system/bands/${bandId}/impersonate`);
      const { magic_url, user_email, expires_in } = r.data;
      const minutes = Math.round(expires_in / 60);
      toast.success(`Opening as ${user_email} — link valid ${minutes} min`);
      window.open(magic_url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    } finally {
      setActionLoading(false);
    }
  };
  const onResendWelcome = (userId, email) => runAction(`Resend welcome to ${email}`,
    () => api.post(`/system/users/${userId}/resend-welcome`));
  const onSendReset = (userId, email) => runAction(`Send reset to ${email}`,
    () => api.post(`/system/users/${userId}/send-password-reset`));
  const onAlignRole = (userId, email) => runAction(`Align role for ${email}`,
    () => api.post(`/system/users/${userId}/align-role?band_id=${bandId}`));

  // iter-104 — daily digest diagnostic
  const loadDigest = async () => {
    if (!bandId) return;
    setDigestLoading(true);
    try {
      const r = await api.get(`/system/bands/${bandId}/daily-digest-diagnostic`);
      setDigest(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load digest diagnostic");
    } finally {
      setDigestLoading(false);
    }
  };
  const sendDigestNow = async () => {
    if (!bandId) return;
    if (!window.confirm("Force-send the daily admin digest now? All band admins will receive an email.")) return;
    setDigestLoading(true);
    try {
      const r = await api.post(`/system/bands/${bandId}/daily-digest/send-now`);
      const sent = r.data?.result?.sent_count || 0;
      toast.success(sent > 0 ? `Sent to ${sent} admin(s)` : "Triggered — no recipients matched");
      await loadDigest();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setDigestLoading(false);
    }
  };

  // iter-105 — Stripe events log
  const loadStripeEvents = async () => {
    if (!bandId) return;
    setStripeLoading(true);
    try {
      const r = await api.get(`/system/bands/${bandId}/stripe-events?limit=50`);
      setStripeEvents(r.data?.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Stripe events");
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto"
        data-testid="band-detail-drawer"
      >
        <SheetHeader className="pb-4 border-b border-white/10 mb-4">
          <SheetTitle className="flex items-center gap-2 text-lg sm:text-2xl break-words">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" />
            <span className="min-w-0 break-words">{data?.name || (loading ? "Loading…" : "Band detail")}</span>
          </SheetTitle>
          {data && (
            <SheetDescription className="flex flex-wrap items-center gap-2 pt-1">
              <StatusBadge status={data.subscription_status} />
              {data.subscription_plan && <Badge variant="outline">{data.subscription_plan}</Badge>}
              <span className="text-xs text-muted-foreground">
                Created {fmtDate(data.created_at)} · ID <code className="text-xs">{data.id?.slice(0, 8)}</code>
              </span>
            </SheetDescription>
          )}
        </SheetHeader>

        {loading && <div className="py-12 text-center text-muted-foreground">Loading band data…</div>}

        {!loading && data && (
          <Tabs defaultValue="overview" className="w-full">
            {/* iter-107r: on mobile only icons are visible (labels are
                hidden until sm:), so all 7 tabs fit on one row. Switching
                from grid-cols-4 (which wrapped tabs 5–7 onto a second
                row that overlapped the digest card below) to grid-cols-7
                everywhere. */}
            <TabsList className="grid grid-cols-7 gap-1 mb-4 h-auto p-1">
              <TabsTrigger value="overview"><Building2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Overview</span></TabsTrigger>
              <TabsTrigger value="users"><Users className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Users</span></TabsTrigger>
              <TabsTrigger value="events"><Calendar className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Events</span></TabsTrigger>
              <TabsTrigger value="chat"><MessageSquare className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Chat</span></TabsTrigger>
              <TabsTrigger value="finance"><Receipt className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Finance</span></TabsTrigger>
              <TabsTrigger
                value="digest"
                data-testid="tab-digest"
                onClick={() => { if (!digest) loadDigest(); }}
              >
                <Activity className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Digest</span>
              </TabsTrigger>
              <TabsTrigger value="audit"><FileClock className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Audit</span></TabsTrigger>
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview" className="space-y-3" data-testid="band-detail-overview">
              {/* Actions panel (iter-103) */}
              <div className="rounded-md border border-white/10 bg-background/30 p-3" data-testid="band-actions-panel">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2 font-semibold">Actions</div>
                <div className="flex flex-wrap gap-2">
                  {data.is_suspended ? (
                    <Button size="sm" variant="outline" onClick={onUnsuspend} disabled={actionLoading} data-testid="action-unsuspend" className="border-emerald-500/40 text-emerald-300">
                      <Play className="w-4 h-4 mr-1" /> Un-suspend
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={onSuspend} disabled={actionLoading} data-testid="action-suspend" className="border-orange-500/40 text-orange-300">
                      <Ban className="w-4 h-4 mr-1" /> Suspend
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onExtendTrial(7)} disabled={actionLoading} data-testid="action-extend-7">
                    <CalendarPlus className="w-4 h-4 mr-1" /> Extend +7d
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onExtendTrial(14)} disabled={actionLoading} data-testid="action-extend-14">
                    <CalendarPlus className="w-4 h-4 mr-1" /> Extend +14d
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onExtendTrial(30)} disabled={actionLoading} data-testid="action-extend-30">
                    <CalendarPlus className="w-4 h-4 mr-1" /> Extend +30d
                  </Button>
                  <Button size="sm" variant="outline" onClick={onImpersonate} disabled={actionLoading} data-testid="action-impersonate" className="border-cyan-500/40 text-cyan-300">
                    <ExternalLink className="w-4 h-4 mr-1" /> Open as admin
                  </Button>
                </div>
                {data.is_suspended && (
                  <div className="mt-2 text-xs text-orange-300 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Band is currently suspended (since {fmt(data.suspended_at)} by {data.suspended_by || "—"}).
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <KV label="Members" value={data.members?.length || 0} />
                <KV label="Users" value={data.users?.length || 0} />
                <KV label="Sections" value={data.section_count || 0} />
                <KV label="Events (total / upcoming)" value={`${data.events_summary?.total || 0} / ${data.events_summary?.upcoming || 0}`} />
                <KV label="Chat rooms / messages" value={`${data.chat?.room_count || 0} / ${data.chat?.message_count || 0}`} />
                <KV label="Invoice outstanding" value={money(data.invoices_summary?.outstanding || 0)} />
                <KV label="Trial end" value={fmtDate(data.trial_end)} />
                <KV label="Subscription end" value={fmtDate(data.subscription_end)} />
                <KV label="Last login" value={data.last_login_at ? `${fmt(data.last_login_at)} (${data.last_login_user})` : "—"} className="col-span-2" />
                {data.admin_notes && <KV label="Admin notes" value={data.admin_notes} className="col-span-2" />}
              </div>

              {/* iter-102: activity timeline */}
              <div className="mt-5">
                <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Activity timeline</h4>
                <ActivityTimeline entries={data.activity_timeline || []} />
              </div>
            </TabsContent>

            {/* Users */}
            <TabsContent value="users" data-testid="band-detail-users">
              {(data.users || []).length === 0 ? <Empty label="No users yet" /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Last login</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.users.map((u) => (
                      <TableRow key={u.id} data-testid={`band-user-${u.id}`}>
                        <TableCell className="text-xs">
                          <code>{u.email}</code>
                          {u.role_conflict && (
                            <div className="text-[10px] text-red-300 mt-0.5 flex items-center gap-1" title={`Top-level role: ${u.legacy_role} · membership role: ${data.users.find((x) => x.id === u.id)?.role_in_band}`}>
                              <AlertTriangle className="w-3 h-3" /> Role conflict
                            </div>
                          )}
                          {u.membership_count_for_band > 1 && (
                            <div className="text-[10px] text-orange-300 mt-0.5 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> {u.membership_count_for_band} membership rows for this band
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{u.name || "—"}</TableCell>
                        <TableCell>
                          {u.role_in_band === "admin"
                            ? <Badge className="bg-primary/20 text-primary"><ShieldCheck className="w-3 h-3 mr-1" />admin</Badge>
                            : <Badge variant="outline">{u.role_in_band || "player"}</Badge>}
                        </TableCell>
                        <TableCell>
                          {u.player_type === "dep" ? (
                            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">dep</Badge>
                          ) : u.player_type === "info_only" ? (
                            <Badge variant="outline" className="text-blue-300 border-blue-500/30">info-only</Badge>
                          ) : u.player_type === "member" ? (
                            <Badge variant="outline" className="text-emerald-300 border-emerald-500/30">member</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">no player row</span>
                          )}
                          {u.player_is_active === false && (
                            <Badge variant="outline" className="text-red-300 border-red-500/30 ml-1">inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{fmt(u.last_login_at)}</TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          {u.role_conflict && (
                            <Button
                              size="sm" variant="outline"
                              className="h-7 px-2 text-xs border-red-500/40 text-red-300"
                              disabled={actionLoading}
                              onClick={() => {
                                if (!window.confirm(`Align ${u.email}'s role? Admin wins by default (legacy=${u.legacy_role || "none"} · membership=${u.role_in_band || "none"})`)) return;
                                onAlignRole(u.id, u.email);
                              }}
                              title="Align legacy + membership role (admin wins)"
                              data-testid={`user-action-align-${u.id}`}
                            >
                              <Wrench className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={actionLoading}
                            onClick={() => onSendReset(u.id, u.email)}
                            title="Send password-reset email"
                            data-testid={`user-action-reset-${u.id}`}
                          >
                            <KeyRound className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={actionLoading}
                            onClick={() => {
                              if (!window.confirm(`Resend welcome to ${u.email}? This will reset their password and send the welcome email.`)) return;
                              onResendWelcome(u.id, u.email);
                            }}
                            title="Resend welcome email (resets password)"
                            data-testid={`user-action-welcome-${u.id}`}
                          >
                            <MailPlus className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Events */}
            <TabsContent value="events" data-testid="band-detail-events">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard label="Total events" value={data.events_summary?.total || 0} />
                <StatCard label="Upcoming" value={data.events_summary?.upcoming || 0} />
                <StatCard label="Last event" value={data.events_summary?.last_event_title || "—"} small />
                <StatCard label="Last event at" value={fmtDate(data.events_summary?.last_event_at)} small />
              </div>
              <p className="text-xs text-muted-foreground">Detailed per-event drill-down lives in the band's own UI — use "View as admin" (coming in iter-103).</p>
            </TabsContent>

            {/* Chat */}
            <TabsContent value="chat" data-testid="band-detail-chat">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard label="Rooms" value={data.chat?.room_count || 0} />
                <StatCard label="Messages" value={data.chat?.message_count || 0} />
                <StatCard label="Last message" value={fmt(data.chat?.last_message_at)} small />
                <StatCard label="Last sender" value={data.chat?.last_sender || "—"} small />
              </div>
            </TabsContent>

            {/* Finance */}
            <TabsContent value="finance" data-testid="band-detail-finance">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard label="Invoices" value={data.invoices_summary?.count || 0} />
                <StatCard label="Total value" value={money(data.invoices_summary?.total_value || 0)} small />
                <StatCard label="Paid" value={money(data.invoices_summary?.paid_value || 0)} small />
                <StatCard label="Outstanding" value={money(data.invoices_summary?.outstanding || 0)} small accent="red" />
              </div>
              <h4 className="text-sm font-medium mb-2 mt-4 flex items-center gap-2">
                <Receipt className="w-4 h-4" /> Subscription transactions
              </h4>
              {(data.transactions || []).length === 0 ? <Empty label="No transactions" /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.transactions.map((t, i) => (
                      <TableRow key={t.session_id || i}>
                        <TableCell className="text-xs">{fmt(t.created_at)}</TableCell>
                        <TableCell>{t.plan || "—"}</TableCell>
                        <TableCell>{money(t.amount || 0, (t.currency || "gbp").toUpperCase())}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={t.payment_status === "paid" ? "text-green-400 border-green-500/30" : ""}>
                            {t.payment_status || t.status || "—"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* iter-105 — Stripe event log */}
              <div className="flex items-center justify-between mt-6 mb-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Receipt className="w-4 h-4" /> Stripe event log
                </h4>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadStripeEvents}
                  disabled={stripeLoading}
                  data-testid="load-stripe-events"
                >
                  {stripeEvents === null ? "Load events" : "Refresh"}
                </Button>
              </div>
              {stripeEvents === null && !stripeLoading && (
                <p className="text-xs text-muted-foreground italic">Click "Load events" to pull the latest 50 Stripe webhook / API events for this band.</p>
              )}
              {stripeLoading && (
                <p className="text-xs text-muted-foreground">Loading…</p>
              )}
              {Array.isArray(stripeEvents) && stripeEvents.length === 0 && (
                <Empty label="No Stripe events recorded for this band yet." />
              )}
              {Array.isArray(stripeEvents) && stripeEvents.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stripeEvents.map((e) => (
                      <TableRow key={e.id} data-testid={`stripe-event-${e.id}`}>
                        <TableCell className="text-xs">{fmt(e.created_at)}</TableCell>
                        <TableCell><code className="text-xs">{e.event_type || "—"}</code></TableCell>
                        <TableCell>
                          <Badge variant="outline" className={e.payment_status === "paid" ? "text-green-400 border-green-500/30" : ""}>
                            {e.payment_status || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {e.amount != null ? money(e.amount, (e.currency || "gbp").toUpperCase()) : "—"}
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{e.source || "—"}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* iter-104: Daily digest diagnostic */}
            <TabsContent value="digest" data-testid="band-detail-digest">
              {digestLoading && !digest && <div className="py-12 text-center text-muted-foreground">Loading digest diagnostic…</div>}
              {!digestLoading && !digest && (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  Click the Digest tab to load diagnostic data.
                </div>
              )}
              {digest && (
                <div className="space-y-4">
                  {/* Header banner */}
                  <div className={`rounded-md border p-3 ${
                    digest.preview?.would_send
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-orange-500/30 bg-orange-500/5"
                  }`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">If we ran the digest right now</div>
                        <div className="font-medium text-base flex items-center gap-2">
                          {digest.preview?.would_send ? (
                            <><CheckCircle className="w-4 h-4 text-emerald-400" /> Would send to {digest.preview?.admins || 0} admin(s)</>
                          ) : (
                            <><XCircle className="w-4 h-4 text-orange-400" /> Would skip — reason: <code className="text-orange-300">{digest.preview?.reason || "unknown"}</code></>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Schedule: {digest.settings?.scheduled_at_uk || "—"} · Enabled: {digest.settings?.daily_digest_enabled ? "yes" : "no"}
                        </div>
                        {/* iter-107o: Brevo sender validation status. If the
                            FROM_EMAIL is not validated/authenticated in
                            Brevo, every transactional send is silently
                            rejected ("Events: Error" in Brevo logs) while
                            our API still returns 201. Surface clearly. */}
                        {/* iter-107w: Scheduler heartbeat + DISABLE_SCHEDULED_EMAILS
                            flag. THE most common silent cause of "digest didn't
                            auto-send" is this env var being inherited from
                            preview into production. Show clearly. */}
                        {digest.scheduler && (
                          <div
                            className={`mt-2 text-xs rounded-md px-3 py-2 border ${
                              digest.scheduler.disable_scheduled_emails
                                ? "bg-red-500/10 border-red-500/40 text-red-300"
                                : digest.scheduler.running
                                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                                  : "bg-amber-500/10 border-amber-500/40 text-amber-300"
                            }`}
                            data-testid="scheduler-status"
                          >
                            <div className="font-semibold mb-0.5">
                              {digest.scheduler.disable_scheduled_emails
                                ? "Scheduler: DISABLED via DISABLE_SCHEDULED_EMAILS env var"
                                : digest.scheduler.running
                                  ? `Scheduler: alive (tick #${digest.scheduler.tick_count ?? "?"}, last at ${fmt(digest.scheduler.last_tick_at)}, London hr ${digest.scheduler.last_tick_london_hour ?? "?"})`
                                  : "Scheduler: not running"}
                            </div>
                            {digest.scheduler.disable_scheduled_emails && (
                              <div className="opacity-90 leading-relaxed">
                                The <code>DISABLE_SCHEDULED_EMAILS</code> env var is set to <code>true</code> on this deployment. Remove it from the production secrets and redeploy — the digest, reminders and monthly summaries will then auto-fire again.
                              </div>
                            )}
                          </div>
                        )}
                        {digest.brevo_sender && (
                          <div
                            className={`mt-2 text-xs rounded-md px-3 py-2 border ${
                              digest.brevo_sender.ok
                                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                                : "bg-red-500/10 border-red-500/40 text-red-300"
                            }`}
                            data-testid="brevo-sender-status"
                          >
                            <div className="font-semibold mb-0.5">
                              Brevo sender: {digest.brevo_sender.ok ? "validated" : "NOT validated"}
                              {digest.brevo_sender.from_email ? ` (${digest.brevo_sender.from_email})` : ""}
                            </div>
                            {!digest.brevo_sender.ok && digest.brevo_sender.error && (
                              <div className="opacity-90 leading-relaxed">
                                {digest.brevo_sender.error}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={sendDigestNow}
                        disabled={digestLoading || actionLoading}
                        data-testid="digest-send-now"
                        className="shrink-0"
                      >
                        <Send className="w-4 h-4 mr-1" /> Send now (force)
                      </Button>
                    </div>
                  </div>

                  {/* Last run */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Last run</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <StatCard label="Last run at" value={fmt(digest.last_run?.run_at)} small />
                      <StatCard label="Last email sent at" value={fmt(digest.last_run?.sent_at)} small />
                      <StatCard label="Last sent count" value={digest.last_run?.result?.sent_count ?? "—"} />
                      <StatCard
                        label="Last skip reason"
                        value={digest.last_run?.result?.skipped_reason || (digest.last_run?.result ? "—" : "never run")}
                        small
                        accent={digest.last_run?.result?.skipped_reason ? "red" : ""}
                      />
                    </div>
                    {digest.last_run?.result?.forced && (
                      <div className="text-xs text-muted-foreground mt-2 italic">Last run was force-triggered (Send now).</div>
                    )}
                    {/* iter-107f: when today's run skipped with no_activity
                        but the preview now has activity, the most likely
                        explanation is that the RSVPs/events landed AFTER
                        the scheduled 4pm UK run. Surface a friendly nudge
                        so admins don't have to piece it together. */}
                    {digest.last_run?.result?.skipped_reason === "no_activity"
                      && ((digest.preview?.pending_events || 0) > 0
                          || (digest.preview?.recent_changes || 0) > 0) && (
                      <div
                        className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-200/90"
                        data-testid="digest-late-activity-banner"
                      >
                        Today&apos;s scheduled run found nothing to report
                        (<code className="text-amber-300">no_activity</code>) and skipped
                        the email. New activity has landed since then —
                        {" "}{(digest.preview?.pending_events || 0)} pending event(s) +
                        {" "}{(digest.preview?.recent_changes || 0)} recent change(s).
                        Click <strong>Send now (force)</strong> above to fire it manually
                        without waiting for tomorrow&apos;s 4pm UK run.
                      </div>
                    )}

                    {/* iter-107j: send_failed means the schedule fired and
                        Rule A or B matched, but every Brevo send call
                        errored. Surface the per-admin error so the user
                        can actually diagnose (Brevo limit, bad sender,
                        network blip, etc.). */}
                    {digest.last_run?.result?.skipped_reason === "send_failed" && (
                      <div
                        className="mt-3 rounded-md border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-200/90"
                        data-testid="digest-send-failed-banner"
                      >
                        <p className="font-semibold mb-1">
                          The scheduled run fired but no email made it out
                          (<code className="text-red-300">send_failed</code>).
                        </p>
                        {Array.isArray(digest.last_run?.result?.send_errors)
                          && digest.last_run.result.send_errors.length > 0 ? (
                          <ul className="mt-2 space-y-1 list-disc list-inside">
                            {digest.last_run.result.send_errors.slice(0, 5).map((e, i) => (
                              <li key={i}>
                                <span className="text-red-100">{e.email || e.admin_id || "—"}</span>
                                {" — "}
                                <span className="text-red-200/80">{e.error}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>No per-admin error captured — check backend logs for &quot;Daily digest send&quot;.</p>
                        )}
                        <p className="mt-2 text-red-100/80">
                          Most common causes: Brevo daily limit hit, sender email unverified,
                          or the admin&apos;s mailbox bouncing. Fix the cause then click
                          <strong> Send now (force)</strong> above to retry.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Preview detail */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Live preview</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <StatCard label="Admins" value={digest.preview?.admins ?? 0} />
                      <StatCard label="Pending events (next 48h)" value={digest.preview?.pending_events ?? 0} />
                      <StatCard label="Recent changes (24h)" value={digest.preview?.recent_changes ?? 0} />
                      <StatCard label="Computed at" value={fmt(digest.preview?.computed_at)} small />
                    </div>
                  </div>

                  {/* Admin recipients */}
                  {Array.isArray(digest.preview?.admin_emails) && digest.preview.admin_emails.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Recipients</h4>
                      <div className="rounded-md border border-white/10 bg-background/30 p-3 text-xs">
                        {digest.preview.admin_emails.map((em) => (
                          <code key={em} className="inline-block mr-2 mb-1 px-2 py-1 rounded bg-background/50">{em}</code>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending events detail */}
                  {Array.isArray(digest.preview?.pending_event_details) && digest.preview.pending_event_details.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Events with pending RSVPs (Rule A)</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>Starts</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {digest.preview.pending_event_details.map((ev) => (
                            <TableRow key={ev.id}>
                              <TableCell className="text-xs">{ev.title || "—"}</TableCell>
                              <TableCell className="text-xs">{fmt(ev.start_time)}</TableCell>
                              <TableCell className="text-right font-medium">{ev.pending_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  <p className="text-[11px] text-muted-foreground italic">
                    Rules: A) Email sent if an event in the next 48h has any non-responder. B) Or if any attendance changed in the last 24h.
                    If neither, the scheduler skips silently — that's expected for quiet bands.
                  </p>

                  {/* iter-135 — last 12 digests sent (monthly member digest
                      + daily-admin force-sends). Support engineers use this
                      to quickly verify whether a "band says they didn't get
                      the digest" complaint is a delivery issue vs a schedule
                      one. */}
                  <div className="pt-2">
                    <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                      Last 12 digests sent
                    </h4>
                    <div
                      className="rounded-md border border-white/10 bg-background/30 max-h-[280px] overflow-y-auto"
                      data-testid="digest-history-panel"
                    >
                      {Array.isArray(data.digest_history) && data.digest_history.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Sent at</TableHead>
                              <TableHead>Kind</TableHead>
                              <TableHead>Subject</TableHead>
                              <TableHead className="text-right">Sent</TableHead>
                              <TableHead className="text-right">Failed</TableHead>
                              <TableHead>By</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.digest_history.map((row, i) => (
                              <TableRow key={`${row.sent_at}-${i}`} data-testid={`digest-history-row-${i}`}>
                                <TableCell className="text-xs whitespace-nowrap">{fmt(row.sent_at)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-[10px]">
                                    {row.kind === "monthly" ? "Monthly" : "Daily admin"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs max-w-[200px] truncate" title={row.subject}>
                                  {row.subject || "—"}
                                </TableCell>
                                <TableCell className="text-right font-medium">{row.sent_count ?? 0}</TableCell>
                                <TableCell className="text-right">
                                  {row.failed_count ? (
                                    <span className="text-red-300 font-medium">{row.failed_count}</span>
                                  ) : (
                                    <span className="text-muted-foreground">0</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">{row.sent_by || "—"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p
                          className="text-center text-xs text-muted-foreground py-6"
                          data-testid="digest-history-empty"
                        >
                          No digests sent to this band yet.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Audit */}
            <TabsContent value="audit" data-testid="band-detail-audit">
              {(data.recent_audit || []).length === 0 ? <Empty label="No system-admin actions on this band yet" /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recent_audit.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell className="text-xs">{fmt(e.created_at)}</TableCell>
                        <TableCell><Badge variant="outline">{e.action}</Badge></TableCell>
                        <TableCell className="text-xs">{e.actor}</TableCell>
                        <TableCell className="text-xs"><code>{e.ip}</code></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

function KV({ label, value, className = "" }) {
  return (
    <div className={`rounded-md border border-white/5 bg-background/40 p-3 ${className}`}>
      <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
        <Mail className="w-3 h-3 opacity-0" />
        {label}
      </div>
      <div className="font-medium break-words text-sm">{value}</div>
    </div>
  );
}

function StatCard({ label, value, small = false, accent = "" }) {
  const tone = accent === "red" ? "text-red-300" : "text-foreground";
  return (
    <Card className="glass-card">
      <CardContent className="p-3 text-center">
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        <div className={`${small ? "text-sm" : "text-2xl font-bold"} ${tone}`}>{value || "—"}</div>
      </CardContent>
    </Card>
  );
}

function Empty({ label }) {
  return <p className="text-center text-muted-foreground py-8 text-sm">{label}</p>;
}
